import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { PrimaryButton } from '../../../../../lib/components/Button/PrimaryButton/PrimaryButton';
import { useAnalyticsLinkActivated } from '../../../analytics/hooks/useAnalyticsLinkActivated';
import { useAnalyticsSetPageInfo } from '../../../analytics/hooks/useAnalyticsSetPageInfo';
import { AuthenticationActions } from '../../state/authenticationActions';
import { selectIsCheckingAuthentication, selectIsLoggedIn } from '../../state/authenticationSelectors';
import { StyledLogin } from './Login.styles';
import { AnalyticsLink } from 'app/cross-cutting-concerns/analytics/interfaces/Analytics.types';
import { RoutePaths } from 'config/route-paths';
import { ButtonBase } from 'lib/components/Button/ButtonBase';
import { LoadingPage } from 'lib/components/LoadingPage/LoadingPage';

export const Login = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCheckingAuthentication = useSelector(selectIsCheckingAuthentication);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const analyticsLinkActivated = useAnalyticsLinkActivated();
  useAnalyticsSetPageInfo({});

  const onLogin = useCallback((): void => {
    analyticsLinkActivated({
      linkName: AnalyticsLink.LOGIN,
    });
    dispatch(AuthenticationActions.signIn());
  }, [analyticsLinkActivated, dispatch]);

  const onRequestService = useCallback((): void => {
    navigate(RoutePaths.SERVICE);
  }, [navigate]);

  useEffect(() => {
    dispatch(AuthenticationActions.checkAuthentication());
  }, [dispatch]);

  if (isCheckingAuthentication) {
    return <LoadingPage />;
  }

  if (isLoggedIn) {
    return <Navigate to={RoutePaths.ROOT} />;
  }

  return (
    <StyledLogin className="login">
      <div className="login__image">
        <div className="login__tint" />
      </div>
      <div className="login__text">
        <div className="login__heading">{t('login.heading')}</div>
        <div className="login__subheading">{t('login.subheading')}</div>
      </div>
      <div className="login__buttons">
        <div className="login__buttons-group">
          <PrimaryButton size="m" className="login__login-button" onClick={onLogin}>
            {t('login.loginButton')}
          </PrimaryButton>
          {process.env.REACT_APP_ENVIRONMENT !== 'prod' && (
            <ButtonBase size="m" className="login__service-button" onClick={onRequestService}>
              {t('login.serviceButton')}
            </ButtonBase>
          )}
        </div>
      </div>
    </StyledLogin>
  );
};
